import React, { useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  Download, Upload, X, FileSpreadsheet, AlertTriangle,
  CheckCircle2, FileWarning, Loader2,
} from "lucide-react";
import * as XLSX from "xlsx";
import apiClient from "../../utils/apiClient";
import { AppButton, AppBadge } from "../ui";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); AIQuestionsImportModal.scss deleted
// with it.
//
// The `[data-theme="dark"]` block at the bottom of that sheet is the clearest
// argument in this batch for the `-fg` half of every status family. It existed
// only to restate three colours — #fca5a5 / #fbbf24 / #6ee7b7 — because the light
// ones (#b91c1c / #b45309 / #047857) were hardcoded. --danger-fg IS 350 90% 78%
// in dark and 350 70% 36% in light, which is those six values exactly, so the
// override block is simply gone.
//
// The modal's own chrome is react-bootstrap's, so the four rules that reshape it
// (`.modal-dialog`, `.modal-content`, and hiding the unused header/footer) are
// arbitrary variants on the Modal's className — markup we render but do not own.
const MODAL =
  "[&_.modal-dialog]:tw-max-w-[640px] " +
  "[&_.modal-content]:tw-overflow-hidden [&_.modal-content]:tw-rounded-[14px] " +
  "[&_.modal-content]:tw-border [&_.modal-content]:tw-border-solid " +
  "[&_.modal-content]:tw-border-line [&_.modal-content]:tw-bg-surface-raised " +
  "[&_.modal-content]:tw-text-content " +
  "[&_.modal-content]:tw-shadow-[0_20px_50px_var(--card-shadow)] " +
  // We render our own header and footer inside the body; Bootstrap's are unused.
  "[&_.modal-header]:tw-hidden [&_.modal-footer]:tw-hidden";

const TILE_ACCENT =
  "tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md " +
  "tw-bg-chart-5/[0.12] tw-text-primary";

const CLOSE_BTN =
  "tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-cursor-pointer tw-items-center " +
  "tw-justify-center tw-rounded-sm tw-border tw-border-solid tw-border-transparent " +
  "tw-bg-transparent tw-text-content-muted " +
  "tw-transition-[background,color,border-color] tw-duration-150 " +
  "hover:tw-border-line hover:tw-bg-surface-hover hover:tw-text-content " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-50";

const CARD =
  "tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-alt " +
  "tw-px-4 tw-py-[0.85rem]";

const CODE_INK =
  "[&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-hover [&_code]:tw-px-[0.35rem] " +
  "[&_code]:tw-py-[0.1rem] [&_code]:tw-text-[0.72rem] [&_code]:tw-text-content";

const DROP =
  "tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-[0.85rem] tw-rounded-md " +
  "tw-border-[1.5px] tw-bg-[var(--input-bg)] tw-p-4 tw-text-left tw-text-content " +
  "tw-transition-[border-color,background] tw-duration-150 " +
  "enabled:hover:tw-border-primary enabled:hover:tw-bg-surface-hover " +
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-60";

const DROP_EMPTY = `${DROP} tw-border-dashed tw-border-line`;
const DROP_FILLED = `${DROP} tw-border-solid tw-border-primary`;

const CALLOUT =
  "tw-flex tw-items-start tw-gap-[0.6rem] tw-rounded-md tw-border tw-border-solid " +
  "tw-px-[0.85rem] tw-py-[0.7rem] tw-text-[0.825rem] tw-leading-[1.5] " +
  "[&>svg]:tw-mt-[0.1rem] [&>svg]:tw-shrink-0 " +
  "[&_ul]:tw-mb-0 [&_ul]:tw-mt-[0.35rem] [&_ul]:tw-ps-[1.1rem] [&_ul]:tw-text-[0.78rem] " +
  "[&_code]:tw-rounded-[4px] [&_code]:tw-bg-surface-hover [&_code]:tw-px-[0.3rem] " +
  "[&_code]:tw-py-[0.05rem] [&_code]:tw-text-[0.72rem]";

const CALLOUT_TONE = {
  danger: `${CALLOUT} tw-border-danger/30 tw-bg-danger/[0.08] tw-text-danger-fg`,
  warning: `${CALLOUT} tw-border-warning/30 tw-bg-warning/[0.08] tw-text-warning-fg`,
  success: `${CALLOUT} tw-border-success/30 tw-bg-success/[0.08] tw-text-success-fg`,
};

const CALLOUT_TITLE = "tw-text-[0.85rem] tw-font-semibold";

const VALID_DIFFICULTIES = ["easy", "medium", "hard"];

// Sample rows shown in the downloadable template — covers multiple tech
// stacks and all difficulty levels so customers see the expected format.
const TEMPLATE_ROWS = [
  { question: "What is a closure in JavaScript? Give a real-world example.", techStack: "JavaScript", difficulty: "easy" },
  { question: "Explain the difference between let, const, and var.", techStack: "JavaScript", difficulty: "easy" },
  { question: "How does the event loop work in Node.js?", techStack: "JavaScript", difficulty: "medium" },
  { question: "Describe how you would optimize a React app that re-renders too often.", techStack: "React", difficulty: "medium" },
  { question: "Compare useMemo and useCallback. When would you avoid them?", techStack: "React", difficulty: "hard" },
  { question: "What is the JVM memory model and how does garbage collection work?", techStack: "Java", difficulty: "medium" },
  { question: "Design a thread-safe LRU cache in Java.", techStack: "Java", difficulty: "hard" },
  { question: "What is the difference between INNER JOIN and LEFT JOIN in SQL?", techStack: "SQL", difficulty: "easy" },
  { question: "Explain database indexing and when it can hurt performance.", techStack: "SQL", difficulty: "medium" },
  { question: "Design a URL shortener like bit.ly. Discuss trade-offs.", techStack: "System Design", difficulty: "hard" },
];

const TEMPLATE_INSTRUCTIONS = [
  ["AI Questions Import — Instructions"],
  [],
  ["1. Use the 'Questions' sheet to add your questions. Do not rename columns."],
  ["2. Required columns:"],
  ["   • question      The interview question text (required)"],
  ["   • techStack     The category / tech stack (e.g. JavaScript, Java, React)"],
  ["   • difficulty    One of: easy, medium, hard (lower-case)"],
  [],
  ["3. Tips:"],
  ["   • Each row = one question."],
  ["   • Keep questions clear and self-contained — students will see them as-is."],
  ["   • Tech stacks you don't have yet will be created automatically."],
  ["   • Rows with missing question / techStack / invalid difficulty are skipped."],
  [],
  ["4. Supported file formats: .xlsx, .xls, .csv"],
  [],
  ["Allowed difficulty values:", "easy", "medium", "hard"],
];

const AIQuestionsImportModal = ({ show, onHide, onImported }) => {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFileName("");
    setParsedRows([]);
    setParseError("");
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onHide();
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Questions (the actual data)
    const wsQuestions = XLSX.utils.json_to_sheet(TEMPLATE_ROWS, {
      header: ["question", "techStack", "difficulty"],
    });
    wsQuestions["!cols"] = [
      { wch: 70 }, // question
      { wch: 18 }, // techStack
      { wch: 12 }, // difficulty
    ];
    XLSX.utils.book_append_sheet(wb, wsQuestions, "Questions");

    // Sheet 2 — Instructions
    const wsInstructions = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS);
    wsInstructions["!cols"] = [{ wch: 60 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");

    XLSX.writeFile(wb, "ai-questions-sample.xlsx");
  };

  const handleFileChange = async (e) => {
    setParseError("");
    setResult(null);
    setParsedRows([]);
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) {
        setParseError("File contains no sheets.");
        return;
      }
      const sheet = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!Array.isArray(rows) || rows.length === 0) {
        setParseError("No rows found in the first sheet.");
        return;
      }
      const normalized = rows.map((r) => {
        const out = { question: "", techStack: "", difficulty: "" };
        for (const key of Object.keys(r)) {
          const k = key.trim().toLowerCase();
          const v = (r[key] ?? "").toString().trim();
          if (k === "question") out.question = v;
          else if (k === "techstack" || k === "tech stack" || k === "category") out.techStack = v;
          else if (k === "difficulty" || k === "level") out.difficulty = v.toLowerCase();
        }
        return out;
      });
      setParsedRows(normalized);
    } catch (err) {
      console.error(err);
      setParseError("Failed to parse the file. Use the provided template (.xlsx or .csv).");
    }
  };

  const previewIssues = parsedRows.reduce(
    (acc, r) => {
      if (!r.question) acc.missingQuestion += 1;
      if (!r.techStack) acc.missingTechStack += 1;
      if (!VALID_DIFFICULTIES.includes(r.difficulty)) acc.invalidDifficulty += 1;
      return acc;
    },
    { missingQuestion: 0, missingTechStack: 0, invalidDifficulty: 0 }
  );

  const validRowCount = parsedRows.filter(
    (r) => r.question && r.techStack && VALID_DIFFICULTIES.includes(r.difficulty)
  ).length;

  const handleImport = async () => {
    if (parsedRows.length === 0) return;
    setImporting(true);
    setResult(null);
    try {
      const { data } = await apiClient.post("/ai/ai-questions/import", {
        questions: parsedRows,
      });
      setResult({ ok: true, ...data });
      if (data?.inserted > 0 && typeof onImported === "function") {
        onImported();
      }
    } catch (err) {
      setResult({ ok: false, message: err?.response?.data?.message || "Import failed." });
    }
    setImporting(false);
  };

  const triggerFilePicker = () => fileInputRef.current?.click();

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={importing ? "static" : true}
      className={MODAL}
      size="lg"
    >
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-5 tw-pb-3 tw-pt-5">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className={`${TILE_ACCENT} tw-h-9 tw-w-9`}>
            <Upload size={18} />
          </div>
          <div>
            <h2 className="tw-m-0 tw-text-[1.05rem] tw-font-semibold tw-text-content">Import AI Questions</h2>
            <p className="tw-mb-0 tw-mt-[0.15rem] tw-text-[0.8rem] tw-text-content-subtle">
              Bulk-add questions from an Excel or CSV file.
            </p>
          </div>
        </div>
        <button
          type="button"
          className={CLOSE_BTN}
          onClick={handleClose}
          disabled={importing}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <div className="tw-flex tw-max-h-[70vh] tw-flex-col tw-gap-[0.85rem] tw-overflow-y-auto tw-p-5">
        {/* Template / instructions card */}
        <div className={CARD}>
          <div className="tw-flex tw-items-center tw-gap-[0.85rem]">
            <div className="tw-inline-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm tw-bg-surface-hover tw-text-content-muted">
              <FileSpreadsheet size={16} />
            </div>
            <div className="tw-min-w-0 tw-flex-1">
              <h3 className="tw-m-0 tw-text-[0.85rem] tw-font-semibold tw-text-content">File format</h3>
              <p className={`tw-mb-0 tw-mt-[0.2rem] tw-text-[0.78rem] tw-leading-[1.45] tw-text-content-subtle ${CODE_INK}`}>
                Required columns: <code>question</code>, <code>techStack</code>,{" "}
                <code>difficulty</code> (easy / medium / hard). Download the sample
                file to see ready-to-edit examples.
              </p>
            </div>
            <AppButton variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download size={14} /> <span>Sample File</span>
            </AppButton>
          </div>
        </div>

        {/* Dropzone-style file picker */}
        <button
          type="button"
          className={fileName ? DROP_FILLED : DROP_EMPTY}
          onClick={triggerFilePicker}
          disabled={importing}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            disabled={importing}
            hidden
          />
          <div className={`${TILE_ACCENT} tw-h-10 tw-w-10`}>
            <Upload size={22} />
          </div>
          <div className="tw-min-w-0 tw-flex-1">
            {fileName ? (
              <>
                <div className="tw-break-all tw-text-sm tw-font-semibold tw-text-content">{fileName}</div>
                <div className="tw-mt-[0.15rem] tw-text-xs tw-text-content-subtle">Click to replace</div>
              </>
            ) : (
              <>
                <div className="tw-break-all tw-text-sm tw-font-semibold tw-text-content">Choose a file to import</div>
                <div className="tw-mt-[0.15rem] tw-text-xs tw-text-content-subtle">.xlsx, .xls, or .csv</div>
              </>
            )}
          </div>
        </button>

        {parseError && (
          <div className={CALLOUT_TONE.danger}>
            <AlertTriangle size={16} />
            <div>{parseError}</div>
          </div>
        )}

        {parsedRows.length > 0 && !result && (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-px-[0.85rem] tw-py-3">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
              <span className="tw-text-[0.8rem] tw-font-medium tw-text-content-muted">Detected</span>
              <AppBadge color="blue" size="sm">{parsedRows.length} row(s)</AppBadge>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
              <span className="tw-text-[0.8rem] tw-font-medium tw-text-content-muted">Valid</span>
              <AppBadge color="emerald" size="sm">{validRowCount}</AppBadge>
            </div>
            {(previewIssues.missingQuestion > 0 ||
              previewIssues.missingTechStack > 0 ||
              previewIssues.invalidDifficulty > 0) && (
              <div className={CALLOUT_TONE.warning}>
                <FileWarning size={16} />
                <div>
                  <div className={CALLOUT_TITLE}>Some rows will be skipped</div>
                  <ul>
                    {previewIssues.missingQuestion > 0 && (
                      <li>{previewIssues.missingQuestion} missing <code>question</code></li>
                    )}
                    {previewIssues.missingTechStack > 0 && (
                      <li>{previewIssues.missingTechStack} missing <code>techStack</code></li>
                    )}
                    {previewIssues.invalidDifficulty > 0 && (
                      <li>{previewIssues.invalidDifficulty} with invalid <code>difficulty</code></li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {result && result.ok && (
          <div className={CALLOUT_TONE.success}>
            <CheckCircle2 size={16} />
            <div>
              <div className={CALLOUT_TITLE}>
                Imported {result.inserted} of {result.total}
              </div>
              {result.skipped > 0 && (
                <div className="tw-mt-[0.15rem] tw-text-[0.78rem] tw-text-content-muted">
                  Skipped {result.skipped} invalid row(s).
                </div>
              )}
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <details className="tw-mt-[0.4rem] [&>summary]:tw-cursor-pointer [&>summary]:tw-text-[0.78rem] [&>summary]:tw-font-medium [&_ul]:tw-mt-[0.35rem] [&_ul]:tw-text-content-muted">
                  <summary>View errors ({result.errors.length})</summary>
                  <ul>
                    {result.errors.map((e, i) => (
                      <li key={i}>Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}

        {result && !result.ok && (
          <div className={CALLOUT_TONE.danger}>
            <AlertTriangle size={16} />
            <div>{result.message}</div>
          </div>
        )}
      </div>

      <div className="tw-flex tw-justify-end tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-px-5 tw-pb-[1.1rem] tw-pt-[0.85rem]">
        <AppButton variant="ghost" onClick={handleClose} disabled={importing}>
          {result?.ok ? "Close" : "Cancel"}
        </AppButton>
        <AppButton
          variant="default"
          onClick={handleImport}
          disabled={importing || parsedRows.length === 0 || !!result?.ok}
        >
          {importing ? (
            <>
              <Loader2 size={14} className="tw-animate-spin" />
              <span>Importing...</span>
            </>
          ) : (
            <>
              <Upload size={14} />
              <span>Import{validRowCount ? ` ${validRowCount}` : ""}</span>
            </>
          )}
        </AppButton>
      </div>
    </Modal>
  );
};

export default AIQuestionsImportModal;
